import { apiFetch, buildResponseError, parseResponseBody } from '../utils/http';
import {
  normalizeAlbumPayload,
  normalizeMediaFilePayload,
  buildMediaCounters,
} from '../components/Mobile/MobileAIStorage/utils';

const PROJECTS_ENDPOINT = '/api/v1/media/ai-storage/projects';
const ASSETS_ENDPOINT = '/api/v1/media/ai-storage/assets';

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '' || value === false) {
      return;
    }
    query.set(key, String(value));
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

const request = async (endpoint, options = {}) => {
  const response = await apiFetch(endpoint, {
    method: options.method || 'GET',
    data: options.data || null,
    headers: options.headers || {},
    isFormData: Boolean(options.isFormData),
    credentials: 'include',
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw buildResponseError(response, body);
  }

  return body ?? {};
};

const normalizeProject = (payload) => {
  const project = payload?.project || payload?.album || payload;
  const normalizedAlbum = normalizeAlbumPayload(project);
  if (!normalizedAlbum) {
    return null;
  }

  const mediaFiles = project?.media_files || project?.files || [];
  const normalizedFiles = mediaFiles
    .map((item) => normalizeMediaFilePayload(item))
    .filter(Boolean);
  const counters = buildMediaCounters(normalizedFiles);

  return {
    ...normalizedAlbum,
    files: normalizedFiles,
    filesCount: normalizedFiles.length || normalizedAlbum.mediaCount || 0,
    imagesCount: counters.imagesCount,
    videosCount: counters.videosCount,
    aiStorage: project?.ai_storage || normalizedAlbum.metadata?.ai_storage || {},
  };
};

const normalizeAsset = (payload) => {
  const asset = payload?.asset || payload?.media_file || payload?.media || payload;
  return normalizeMediaFilePayload(asset);
};

class AIStorageService {
  async getProjects(params = {}) {
    const query = buildQuery({
      page: params.page || 1,
      page_size: params.pageSize || 50,
      include_archived: params.includeArchived || false,
      search: params.search,
      tag: params.tag,
      preview_limit: params.previewLimit || 8,
    });
    const response = await request(`${PROJECTS_ENDPOINT}${query}`);
    return (response.items || []).map((item) => normalizeProject(item)).filter(Boolean);
  }

  async getProject(projectId, params = {}) {
    const query = buildQuery({
      asset_search: params.search,
      asset_tag: params.tag,
      media_type: params.mediaType,
      subtype: params.subtype,
      asset_origin: params.assetOrigin,
      lifecycle: params.lifecycle,
    });
    const response = await request(`${PROJECTS_ENDPOINT}/${projectId}${query}`);
    return normalizeProject(response);
  }

  async createProject(projectData) {
    const response = await request(PROJECTS_ENDPOINT, {
      method: 'POST',
      data: {
        title: projectData.title,
        description: projectData.description || null,
        tags: projectData.tags || [],
        metadata: projectData.metadata || {},
      },
    });
    return normalizeProject(response);
  }

  async updateProject(projectId, projectData) {
    const response = await request(`${PROJECTS_ENDPOINT}/${projectId}`, {
      method: 'PUT',
      data: {
        title: projectData.title,
        description: projectData.description || null,
        tags: projectData.tags,
        metadata: projectData.metadata,
        is_archived: projectData.isArchived,
      },
    });
    return normalizeProject(response);
  }

  async deleteProject(projectId) {
    await request(`${PROJECTS_ENDPOINT}/${projectId}`, {
      method: 'DELETE',
    });
  }

  async uploadFiles(projectId, files) {
    const fileList = Array.from(files || []).filter(Boolean);
    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('media_type', file.type.startsWith('video/') ? 'video' : 'image');
      formData.append('subtype', file.type.startsWith('video/') ? 'uploaded' : 'uploaded');
      if (file.size) {
        formData.append('size_kb', String(Math.round(file.size / 1024)));
      }
      await request(`${PROJECTS_ENDPOINT}/${projectId}/assets/upload`, {
        method: 'POST',
        data: formData,
        isFormData: true,
      });
    }
    return this.getProject(projectId);
  }

  async attachAssets(projectId, mediaIds) {
    const response = await request(`${PROJECTS_ENDPOINT}/${projectId}/assets`, {
      method: 'POST',
      data: { media_ids: mediaIds },
    });
    return normalizeProject(response);
  }

  async removeAsset(projectId, assetId) {
    const response = await request(`${PROJECTS_ENDPOINT}/${projectId}/assets/${assetId}`, {
      method: 'DELETE',
    });
    return normalizeProject(response);
  }

  async listAssets(params = {}) {
    const query = buildQuery({
      project_id: params.projectId,
      page: params.page || 1,
      page_size: params.pageSize || 50,
      search: params.search,
      tag: params.tag,
      media_type: params.mediaType,
      subtype: params.subtype,
      asset_origin: params.assetOrigin,
      lifecycle: params.lifecycle,
      include_debug: params.includeDebug || false,
    });
    const response = await request(`${ASSETS_ENDPOINT}${query}`);
    return {
      items: (response.items || []).map((item) => normalizeAsset(item)).filter(Boolean),
      pagination: response.pagination || null,
    };
  }

  async updateAsset(assetId, payload) {
    const response = await request(`${ASSETS_ENDPOINT}/${assetId}`, {
      method: 'PATCH',
      data: {
        name: payload.name,
        description: payload.description,
        prompt: payload.prompt,
        prompt_json: payload.promptJson,
        subtype: payload.subtype,
        tags: payload.tags,
        asset_origin: payload.assetOrigin,
        lifecycle: payload.lifecycle,
        version: payload.version,
        reuse: payload.reuse,
        metadata: payload.metadata,
      },
    });
    return normalizeAsset(response);
  }

  async transcribeAudio() {
    throw new Error('Audio transcription is not implemented in AI Storage service');
  }
}

const aiStorageService = new AIStorageService();

export default aiStorageService;
